<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import RealEstateCommissionPaymentFields from './RealEstateCommissionPaymentFields.vue';
import RealEstateCommissionPaymentMethod from './RealEstateCommissionPaymentMethod.vue';
import {
  formatPixDocument,
  isValidPixDocument,
  pixDocumentKind,
} from '../composables/commissionPaymentValidation';

const props = defineProps({
  banks: { type: Array, default: () => [] },
  pixKeys: { type: Array, default: () => [] },
  recipientName: { type: String, default: '' },
  row: { type: Object, default: null },
  show: { type: Boolean, default: false },
  title: { type: String, default: '' },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();
const draft = ref({});

watch(
  () => props.row,
  value => {
    draft.value = { ...(value || {}) };
  },
  { immediate: true, deep: true }
);

const bankPayment = computed(() => draft.value.tipo_pagamento === 'banco');
const pixDocumentKey = computed(
  () => String(draft.value.tipo_chave_pix_id) === '1'
);
const pixDocumentInvalid = computed(
  () =>
    pixDocumentKey.value &&
    Boolean(draft.value.chave_pix) &&
    !isValidPixDocument(draft.value.chave_pix)
);
const pixDocumentError = computed(() => {
  if (!pixDocumentInvalid.value) return '';
  return pixDocumentKind(draft.value.chave_pix) === 'cnpj'
    ? t('REAL_ESTATE_COMMISSION_INSTALLMENT.PAYMENT.CNPJ_INVALID')
    : t('REAL_ESTATE_COMMISSION_INSTALLMENT.PAYMENT.CPF_INVALID');
});
const valid = computed(() => {
  if (bankPayment.value) {
    return Boolean(
      draft.value.banco_id && draft.value.agencia && draft.value.numero_conta
    );
  }

  return Boolean(
    draft.value.tipo_chave_pix_id &&
      draft.value.chave_pix &&
      !pixDocumentInvalid.value
  );
});

const selectBank = event => {
  const id = event.target.value;
  const bank = props.banks.find(item => String(item.id) === String(id));
  draft.value = {
    ...draft.value,
    banco_id: id,
    nome_banco: bank?.nome || bank?.name || '',
  };
};

const selectPixKeyType = event => {
  const type = event.target.value;
  draft.value = {
    ...draft.value,
    tipo_chave_pix_id: type,
    chave_pix:
      String(type) === '1'
        ? formatPixDocument(draft.value.chave_pix)
        : draft.value.chave_pix,
  };
};

const selectPaymentMethod = type => {
  draft.value = { ...draft.value, tipo_pagamento: type };
};

const updateField = (field, value) => {
  draft.value = { ...draft.value, [field]: value };
};

const updatePixKey = event => {
  const value = event.target.value;
  draft.value = {
    ...draft.value,
    chave_pix: pixDocumentKey.value ? formatPixDocument(value) : value,
  };
};

const save = () => {
  if (!valid.value) return;
  emit('save', { ...draft.value });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="title"
    max-width-class="max-w-[560px]"
    @close="emit('close')"
  >
    <div class="grid gap-4">
      <div
        v-if="recipientName"
        class="rounded-lais border border-lais-border-soft bg-lais-bg-soft p-3"
      >
        <p class="text-xs text-lais-text-muted">
          {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.PAYMENT.BENEFICIARY') }}
        </p>
        <p class="mt-1 text-sm font-semibold text-lais-text">
          {{ recipientName }}
        </p>
      </div>
      <p class="text-sm font-semibold text-lais-text">
        {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.PAYMENT.BANK_DETAILS') }}
      </p>
      <RealEstateCommissionPaymentMethod
        :model-value="draft.tipo_pagamento"
        @update:model-value="selectPaymentMethod"
      />
      <RealEstateCommissionPaymentFields
        :banks="banks"
        :pix-keys="pixKeys"
        :draft="draft"
        :bank-payment="bankPayment"
        :pix-document-error="pixDocumentError"
        @select-bank="selectBank"
        @select-pix-key="selectPixKeyType"
        @update-field="updateField"
        @update-pix-key="updatePixKey"
      />
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.ACTIONS.BACK') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton :disabled="!valid" @click="save">
          {{ t('REAL_ESTATE_COMMISSION_INSTALLMENT.ACTIONS.SAVE_PAYMENT') }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
